// The Minister's monthly brief: one printable A4 page summarising the last 30 days — screenings,
// teleconsults, urgent flags and response, district comparison, kiosk fleet and items needing a decision.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { staff } from '../lib/api.ts';
import { Login, useStaffSession } from '../ui/staff.tsx';
import { PROGRAM } from '../ui/site.tsx';

interface District { id: string; name: string; name_ur: string; division: string; remote: number; facilities: number; kiosks: number; kiosks_online: number; screenings: number; consults: number; urgent: number; open_flags: number }
interface Measures {
  access: { completedSessions: number; teleconsults: number; teleconsultUptake: number };
  flow: { avgSessionMinutes: number | null; avgConsultWaitMinutes: number | null; completionRate: number | null };
  safety: { qualityRetryRate: number; urgentFlags: number; unresolvedFlags: number; avgUrgentResponseMinutes: number | null; clinicianOverrides: number };
  equity: { bySex: { sex: string; n: number }[]; assistedShare: number };
}
interface Kiosk { id: string; facility: string; district: string; online: number; alerts: string[] }
interface Flag { severity: string; created_at: string; district: string }

const DAYS = 30;
const n = (v: number | null | undefined, d = 0) => (v == null ? '—' : v.toLocaleString('en-PK', { maximumFractionDigits: d, minimumFractionDigits: d }));
const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Karachi' });

export function BriefPage() {
  const { user, checking, setUser, logout } = useStaffSession();
  const [data, setData] = useState<{ districts: District[]; measures: Measures; fleet: Kiosk[]; flags: Flag[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    if (user.role === 'support' || user.role === 'clinician') { setError('The monthly brief is available to program, district and facility officers.'); return; }
    Promise.all([
      staff.get<District[]>(`/api/dashboard/districts?days=${DAYS}`),
      staff.get<Measures>(`/api/dashboard/measures?days=${DAYS}`),
      staff.get<Kiosk[]>('/api/dashboard/fleet'),
      staff.get<Flag[]>('/api/flags'),
    ]).then(([districts, measures, fleet, flags]) => setData({ districts, measures, fleet, flags })).catch(() => setError('Could not load the brief.'));
  }, [user]);

  if (checking) return null;
  if (!user) return <Login surface="brief" title="Minister’s monthly brief" onLogin={setUser} />;

  const today = new Date();
  const from = new Date(today.getTime() - (DAYS - 1) * 86400_000);
  const scope = user.role === 'program' ? 'All ten districts of Azad Jammu & Kashmir' : user.district ? `${user.district} district` : user.facility ?? '';

  const attention: string[] = [];
  if (data) {
    const m = data.measures;
    const offline = data.fleet.filter((k) => !k.online);
    const staleUrgent = data.flags.filter((f) => f.severity === 'urgent' && Date.now() - Date.parse(f.created_at) > 24 * 3600_000);
    if (staleUrgent.length) attention.push(`${staleUrgent.length} urgent flag(s) open for more than 24 hours — District Health Officers to confirm follow-up.`);
    if (m.safety.avgUrgentResponseMinutes != null && m.safety.avgUrgentResponseMinutes > 30) attention.push(`Average response to urgent flags is ${n(m.safety.avgUrgentResponseMinutes)} minutes, above the 30-minute target.`);
    if (offline.length) attention.push(`${offline.length} kiosk(s) offline: ${offline.slice(0, 4).map((k) => k.facility).join(', ')}${offline.length > 4 ? '…' : ''}. MOVO-X field team dispatched under the service levels.`);
    const other = data.fleet.filter((k) => k.online && k.alerts.length);
    if (other.length) attention.push(`${other.length} kiosk(s) need consumables or maintenance (paper, calibration or sensors).`);
    const low = [...data.districts].filter((d) => d.kiosks > 0).sort((a, b) => a.screenings / a.kiosks - b.screenings / b.kiosks)[0];
    if (low && data.districts.length > 1) attention.push(`Lowest use per kiosk: ${low.name} (${n(low.screenings / low.kiosks)} screenings per kiosk) — community awareness session recommended.`);
    if (m.flow.completionRate != null && m.flow.completionRate < 0.85) attention.push(`Session completion is ${pct(m.flow.completionRate)}; review of assisted-mode staffing recommended.`);
    if (!attention.length) attention.push('No items require a decision this month.');
  }

  const totals = data?.districts.reduce((a, d) => ({ kiosks: a.kiosks + d.kiosks, online: a.online + d.kiosks_online, screenings: a.screenings + d.screenings, consults: a.consults + d.consults, urgent: a.urgent + d.urgent, open: a.open + d.open_flags }), { kiosks: 0, online: 0, screenings: 0, consults: 0, urgent: 0, open: 0 });
  const female = data?.measures.equity.bySex.find((s) => s.sex === 'female')?.n ?? 0;
  const allSex = data?.measures.equity.bySex.reduce((a, s) => a + s.n, 0) ?? 0;

  return (
    <div className="brief">
      <div className="brief-toolbar">
        <Link to="/ministry" className="btn btn-small">← Ministry &amp; governance</Link>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-green" onClick={() => window.print()}>Print / save as PDF</button>
          <button className="btn btn-small" onClick={logout}>Sign out</button>
        </div>
      </div>
      <article className="brief-page">
        <header className="brief-head">
          <img src="/brand/seal.svg" alt="" />
          <div>
            <div style={{ fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gov-gold-soft)' }}>{PROGRAM.en} · {PROGRAM.sub}</div>
            <h1>Monthly brief for the Minister of Health</h1>
            <div className="ur" lang="ur" dir="rtl">{PROGRAM.programUr} — وزیر صحت کے لیے ماہانہ رپورٹ</div>
          </div>
          <div className="meta">
            <div><strong>{fmtDate(from)} – {fmtDate(today)}</strong></div>
            <div>{scope}</div>
            <div>Prepared {fmtDate(today)} · {user.name}</div>
          </div>
        </header>

        <div className="brief-body">
          {error && <div className="callout">{error}</div>}
          {!data && !error && <p className="muted">Preparing the brief…</p>}
          {data && totals && (
            <>
              <section>
                <h2>1 · The month at a glance</h2>
                <div className="brief-kpis">
                  <div className="brief-kpi"><div className="v">{n(data.measures.access.completedSessions)}</div><div className="l">Screenings completed</div></div>
                  <div className="brief-kpi"><div className="v">{n(data.measures.access.teleconsults)}</div><div className="l">Teleconsults · {pct(data.measures.access.teleconsultUptake)} uptake</div></div>
                  <div className="brief-kpi"><div className="v">{n(data.measures.safety.urgentFlags)}</div><div className="l">Urgent flags · {n(data.measures.safety.unresolvedFlags)} open</div></div>
                  <div className="brief-kpi"><div className="v">{totals.kiosks ? pct(totals.online / totals.kiosks) : '—'}</div><div className="l">Kiosks online now · {totals.online}/{totals.kiosks}</div></div>
                  <div className="brief-kpi"><div className="v">{n(data.measures.flow.avgSessionMinutes, 1)}</div><div className="l">Minutes per screening</div></div>
                  <div className="brief-kpi"><div className="v">{n(data.measures.flow.avgConsultWaitMinutes, 0)}</div><div className="l">Minutes wait for doctor</div></div>
                  <div className="brief-kpi"><div className="v">{n(data.measures.safety.avgUrgentResponseMinutes)}</div><div className="l">Minutes to act on urgent flag</div></div>
                  <div className="brief-kpi"><div className="v">{allSex ? pct(female / allSex) : '—'}</div><div className="l">Women among patients</div></div>
                </div>
              </section>

              <section>
                <h2>2 · Districts</h2>
                <table className="brief-table">
                  <thead><tr><th>District</th><th>Division</th><th className="num">Kiosks</th><th className="num">Online</th><th className="num">Screenings</th><th className="num">Teleconsults</th><th className="num">Urgent flags</th><th className="num">Open flags</th></tr></thead>
                  <tbody>
                    {data.districts.map((d) => (
                      <tr key={d.id}><td><strong>{d.name}</strong>{d.remote ? ' ·  remote' : ''}</td><td>{d.division}</td><td className="num">{d.kiosks}</td><td className="num">{d.kiosks_online}</td><td className="num">{n(d.screenings)}</td><td className="num">{n(d.consults)}</td><td className="num">{d.urgent}</td><td className="num">{d.open_flags}</td></tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td><strong>Total</strong></td><td /><td className="num"><strong>{totals.kiosks}</strong></td><td className="num"><strong>{totals.online}</strong></td><td className="num"><strong>{n(totals.screenings)}</strong></td><td className="num"><strong>{n(totals.consults)}</strong></td><td className="num"><strong>{totals.urgent}</strong></td><td className="num"><strong>{totals.open}</strong></td></tr></tfoot>
                </table>
              </section>

              <section>
                <h2>3 · Clinical safety and quality</h2>
                <table className="brief-table">
                  <tbody>
                    <tr><td>Session completion rate</td><td className="num">{pct(data.measures.flow.completionRate)}</td><td>Readings repeated for quality</td><td className="num">{pct(data.measures.safety.qualityRetryRate)}</td></tr>
                    <tr><td>Clinician overrides of decision support (with reason)</td><td className="num">{data.measures.safety.clinicianOverrides}</td><td>Staff-assisted sessions</td><td className="num">{pct(data.measures.equity.assistedShare)}</td></tr>
                  </tbody>
                </table>
              </section>

              <section>
                <h2>4 · Items for the Minister’s attention</h2>
                <ol className="brief-attn">{attention.map((a) => <li key={a}>{a}</li>)}</ol>
              </section>

              <section>
                <div className="brief-sign">
                  <div>Program focal person, Directorate General Health Services</div>
                  <div>Secretary Health, Government of Azad Jammu &amp; Kashmir</div>
                </div>
                <p className="muted small" style={{ marginTop: 14 }}>
                  Generated from the program dashboard ({DAYS}-day window). Demonstration figures are synthetic. Thresholds are draft (AJK-DRAFT-0.1) pending sign-off by the clinical lead.
                </p>
              </section>
            </>
          )}
        </div>
      </article>
    </div>
  );
}
